import React, { useEffect, useRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import API from 'src/api/config';

type RecordingState = 'idle' | 'recording' | 'transcribing';

interface DictateButtonProps {
  /** Called with the recognized text once Whisper returns. The current block
   * decides whether to append, replace, or insert at cursor. */
  onTranscript: (text: string) => void;
  /** BCP-47 language hint passed to Whisper. Defaults to 'en'. */
  language?: string;
  /** Tooltip / aria label overrides. */
  idleLabel?: string;
  recordingLabel?: string;
  /** "muted" sits unobtrusively inside content (e.g. per-block dictation);
   * "primary" is a more prominent purple button. Defaults to "primary". */
  variant?: 'primary' | 'muted';
}

/**
 * Press the mic to record, press again to stop. Audio is POSTed to the
 * server-side /ai/transcribe endpoint, which proxies to OpenAI Whisper.
 *
 * Uses MediaRecorder with the browser's preferred container/codec
 * (typically webm/opus in Chrome, mp4 in Safari) — Whisper accepts both.
 */
export default function DictateButton({
  onTranscript,
  language = 'en',
  idleLabel,
  recordingLabel,
  variant = 'primary',
}: DictateButtonProps) {
  const [state, setState] = useState<RecordingState>('idle');
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  // Always tear down the mic stream on unmount — leaving it open keeps the
  // browser's "recording" indicator on, which is alarming for kids.
  useEffect(() => () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
  };

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (state === 'recording') {
      stopRecording();
      return;
    }
    if (state === 'transcribing') return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      // Pick a supported mime type. Chrome/Edge: webm/opus. Safari: mp4.
      const preferred = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus',
      ];
      const mimeType = preferred.find(
        (m) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m),
      );

      const recorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined,
      );
      recorderRef.current = recorder;

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        // Release the mic immediately so the browser indicator goes away.
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;

        const blob = new Blob(chunksRef.current, {
          type: mimeType || 'audio/webm',
        });
        if (blob.size === 0) {
          setState('idle');
          return;
        }

        setState('transcribing');
        try {
          const form = new FormData();
          // Filename extension just helps the server-side multipart parser;
          // Whisper sniffs the actual container itself.
          const ext = (mimeType || '').includes('mp4') ? 'm4a' : 'webm';
          form.append('audio', blob, `dictation.${ext}`);
          form.append('language', language);

          const response = await API.post('/ai/transcribe', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          const text = response.data?.text || '';
          if (text) {
            onTranscript(text);
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Transcription failed', err);
        } finally {
          setState('idle');
        }
      };

      recorder.start();
      setState('recording');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Mic permission denied or unavailable', err);
      setState('idle');
    }
  };

  const tooltipLabel = (() => {
    if (state === 'recording') return recordingLabel ?? 'Stop & transcribe';
    if (state === 'transcribing') return 'Transcribing…';
    return idleLabel ?? 'Speak your writing';
  })();

  const idleColor = variant === 'muted' ? '#9A9A9A' : '#5C4FB8';
  const hoverColor = variant === 'muted' ? '#666666' : '#5C4FB8';
  const idleHoverBg = variant === 'muted' ? 'rgba(0,0,0,.06)' : 'rgba(92,79,184,.08)';
  const spinnerColor = variant === 'muted' ? '#666666' : '#5C4FB8';

  return (
    <Tooltip title={tooltipLabel}>
      <span>
        <IconButton
          onClick={handleClick}
          size="small"
          aria-label={tooltipLabel}
          disabled={state === 'transcribing'}
          sx={{
            color: state === 'recording' ? '#C8102E' : idleColor,
            backgroundColor: state === 'recording' ? 'rgba(200,16,46,.08)' : 'transparent',
            padding: '6px',
            transition: 'background-color .15s ease-out, color .15s ease-out',
            '&:hover': {
              color: state === 'recording' ? '#C8102E' : hoverColor,
              backgroundColor: state === 'recording'
                ? 'rgba(200,16,46,.16)'
                : idleHoverBg,
            },
          }}
        >
          {state === 'transcribing' ? (
            <CircularProgress size={18} thickness={5} sx={{ color: spinnerColor }} />
          ) : state === 'recording' ? (
            <StopIcon fontSize="small" />
          ) : (
            <MicIcon fontSize="small" />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
}
